import React from 'react';
import { Document, Page } from 'react-pdf';
import { formatSignDate, collectTextOverlaysForPage } from './utils';
import { DEFAULT_SIGNATURE_WIDTH_PCT } from './constants';

function PDFViewer({
  pdfUrl,
  numPages,
  error,
  isLoadingPdf,
  pdfContainerWidth,
  pdfContainerRef,
  isPreviewMode,
  placementTarget,
  signatureDraft,
  signaturePosition,
  textBlocks,
  pdfPageRefs,
  pdfPages,
  onDocumentLoadSuccess,
  onDocumentLoadError,
  onPlacementClick,
  registerPageRef,
  onRetry,
}) {
  // Resolve signature overlay for page
  const resolveSignatureOverlay = (pageNumber) => {
    if (!signatureDraft || !signaturePosition) return null;
    if (signaturePosition.pageNumber !== pageNumber) return null;
    return {
      image: signatureDraft,
      placement: signaturePosition,
    };
  };

  // Get text overlays for page
  const getActiveTextOverlays = (pageNumber) => collectTextOverlaysForPage(textBlocks, pageNumber);

  // Compose overlay style
  const composeOverlayStyle = (placement, widthFallback) => {
    if (placement.canvasXPct !== undefined && placement.canvasYPct !== undefined) {
      return {
        position: 'absolute',
        top: `${placement.canvasYPct * 100}%`,
        left: `${placement.canvasXPct * 100}%`,
        transform: 'translateY(-100%)',
        width: `${((placement.widthPct ?? widthFallback) * 100).toFixed(2)}%`,
      };
    }
    return {
      position: 'absolute',
      top: `${placement.yPct * 100}%`,
      left: `${placement.xPct * 100}%`,
      transform: 'translateY(-100%)',
      width: `${((placement.widthPct ?? widthFallback) * 100).toFixed(2)}%`,
    };
  };

  return (
    <div
      ref={pdfContainerRef}
      data-tour="pdf-viewer"
      className={`${isPreviewMode ? 'w-full' : 'flex-1'} overflow-y-auto bg-gray-50 py-2 sm:py-4 thin-scrollbar`}
      style={{
        scrollbarWidth: 'thin',
        scrollbarColor: '#cbd5e1 #f1f5f9',
        minWidth: '300px',
      }}
    >
      {isLoadingPdf && !pdfUrl && (
        <div className="flex items-center justify-center h-full">
          <div className="text-center">
            <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500 mb-4"></div>
            <p className="text-gray-600">Loading document...</p>
          </div>
        </div>
      )}

      {error && !pdfUrl && !isLoadingPdf && (
        <div className="flex items-center justify-center h-full">
          <div className="text-center">
            <p className="text-red-600 mb-4">{error}</p>
            <button
              onClick={onRetry}
              className="px-4 py-2 bg-blue-500 text-white rounded-md hover:bg-blue-600"
            >
              Retry
            </button>
          </div>
        </div>
      )}

      {pdfUrl && (
        <div
          className="mx-auto mb-4 sm:mb-6"
          style={{
            border: '1px solid #ccc',
            borderRadius: 8,
            padding: '8px',
            background: '#fefefe',
            width: 'fit-content',
            maxWidth: '95%',
            minWidth: '280px',
          }}
        >
          <Document
            key={pdfUrl}
            file={pdfUrl}
            onLoadSuccess={onDocumentLoadSuccess}
            onLoadError={onDocumentLoadError}
            externalLinkTarget="_blank"
            loading={
              <div className="flex items-center justify-center h-64">
                <div className="text-center">
                  <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500 mb-4"></div>
                  <p className="text-gray-600">Loading document…</p>
                </div>
              </div>
            }
            error={
              <div className="flex items-center justify-center h-64">
                <div className="text-center">
                  <p className="text-red-600 mb-2">Failed to load PDF</p>
                  <button
                    onClick={onRetry}
                    className="px-4 py-2 bg-blue-500 text-white rounded-md hover:bg-blue-600"
                  >
                    Retry
                  </button>
                </div>
              </div>
            }
          >
            {numPages > 0 &&
              Array.from({ length: numPages }, (_, index) => {
                const pageNumber = index + 1;
                const signatureOverlay = resolveSignatureOverlay(pageNumber);
                const textOverlays = getActiveTextOverlays(pageNumber);

                return (
                  <div
                    key={`page_${pageNumber}`}
                    ref={(node) => registerPageRef(pageNumber, node)}
                    className={`pdf-page-wrapper${placementTarget ? ' placing' : ''}`}
                    onClick={!isPreviewMode ? (event) => onPlacementClick(pageNumber, event) : undefined}
                    style={{
                      position: 'relative',
                      cursor: !isPreviewMode && placementTarget ? 'crosshair' : 'default',
                    }}
                  >
                    <Page
                      pageNumber={pageNumber}
                      renderTextLayer={false}
                      renderAnnotationLayer
                      className="pdf-page"
                      width={pdfContainerWidth}
                    />
                    {!isPreviewMode && signatureOverlay && (() => {
                      const wrapperEl = pdfPageRefs.current[pageNumber];
                      const canvas = wrapperEl?.querySelector('.react-pdf__Page__canvas');

                      if (canvas && signatureOverlay.placement.canvasXPct !== undefined) {
                        const canvasRect = canvas.getBoundingClientRect();
                        const wrapperRect = wrapperEl.getBoundingClientRect();

                        const canvasOffsetX = canvasRect.left - wrapperRect.left;
                        const canvasOffsetY = canvasRect.top - wrapperRect.top;
                        const left = canvasOffsetX + (signatureOverlay.placement.canvasXPct * canvasRect.width);
                        const top = canvasOffsetY + (signatureOverlay.placement.canvasYPct * canvasRect.height);
                        const width = (signatureOverlay.placement.widthPct ?? DEFAULT_SIGNATURE_WIDTH_PCT) * canvasRect.width;

                        return (
                          <div
                            className="signature-overlay"
                            style={{
                              position: 'absolute',
                              left: `${left}px`,
                              top: `${top}px`,
                              transform: 'translateY(-100%)',
                              width: `${width}px`,
                              pointerEvents: 'none',
                              lineHeight: 0,
                            }}
                          >
                            <img
                              src={signatureOverlay.image}
                              alt="Signature"
                              className="max-w-full h-auto block"
                            />
                          </div>
                        );
                      }

                      return (
                        <div
                          className="signature-overlay"
                          style={{
                            ...composeOverlayStyle(
                              signatureOverlay.placement,
                              signatureOverlay.placement.widthPct ?? DEFAULT_SIGNATURE_WIDTH_PCT
                            ),
                                                        pointerEvents: 'none',
                            lineHeight: 0,
                          }}
                        >
                          <img
                            src={signatureOverlay.image}
                            alt="Signature"
                            className="max-w-full h-auto block"
                          />
                        </div>
                      );
                    })()}
                    {!isPreviewMode && textOverlays.map((overlay) => {
                      const wrapperEl = pdfPageRefs.current[pageNumber];
                      const canvas = wrapperEl?.querySelector('.react-pdf__Page__canvas');

                      if (canvas && overlay.placement?.canvasXPct !== undefined) {
                        const canvasRect = canvas.getBoundingClientRect();
                        const wrapperRect = wrapperEl.getBoundingClientRect();

                        const canvasOffsetX = canvasRect.left - wrapperRect.left;
                        const canvasOffsetY = canvasRect.top - wrapperRect.top;
                        const width = (overlay.placement?.widthPct ?? overlay.widthPct ?? DEFAULT_SIGNATURE_WIDTH_PCT) * canvasRect.width;
                        
                        // Position text starting from the click point (left-aligned)
                        // canvasXPct is the click point where text should start
                        const startX = canvasOffsetX + (overlay.placement.canvasXPct * canvasRect.width);
                        const top = canvasOffsetY + (overlay.placement.canvasYPct * canvasRect.height);

                        return (
                          <div
                            key={`${overlay.id}-${pageNumber}`}
                            className="signature-overlay text-sm"
                            style={{
                              position: 'absolute',
                              left: `${startX}px`,
                              top: `${top}px`,
                              transform: 'translateY(-100%)',
                              width: `${width}px`,
                              pointerEvents: 'none',
                              color: '#000000',
                              textAlign: 'left',
                              whiteSpace: 'nowrap',
                              overflow: 'hidden',
                              textOverflow: 'ellipsis',
                              lineHeight: 1,
                            }}
                          >
                            {overlay.type === 'date' ? formatSignDate(overlay.text) : overlay.text}
                          </div>
                        );
                      }

                      // Fallback: use percentage-based positioning
                      const widthPct = overlay.placement?.widthPct ?? overlay.widthPct ?? DEFAULT_SIGNATURE_WIDTH_PCT;
                      const startXPct = overlay.placement.xPct ?? 0.5;
                      
                      return (
                        <div
                          key={`${overlay.id}-${pageNumber}`}
                          className="signature-overlay text-sm"
                          style={{
                            position: 'absolute',
                            top: `${(overlay.placement.yPct ?? 0.5) * 100}%`,
                            left: `${startXPct * 100}%`,
                            transform: 'translateY(-100%)',
                            width: `${widthPct * 100}%`,
                            pointerEvents: 'none',
                            color: '#000000',
                            textAlign: 'left',
                            whiteSpace: 'nowrap',
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            lineHeight: 1,
                          }}
                        >
                          {overlay.type === 'date' ? formatSignDate(overlay.text) : overlay.text}
                        </div>
                      );
                    })}
                  </div>
                );
              })}
          </Document>
        </div>
      )}

      {!pdfUrl && !error && (
        <div className="flex items-center justify-center h-full">
          <div className="text-center">
            <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500 mb-4"></div>
            <p className="text-gray-600">Preparing document...</p>
          </div>
        </div>
      )}
    </div>
  );
}

export default PDFViewer;
